import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
  ListSubheader,
  Avatar,
  Menu,
  Tooltip
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  BarChart as BarChartIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Settings as CogsIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AccessTime as AccessTimeIcon,
  TableChart,
  List as ListIcon,
  AccountCircle,
  EventAvailable,
  Badge,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon
} from "@mui/icons-material";
import { styled, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { buildAppTheme } from '../theme/appTheme';
import { cssVariables } from '../theme/tokens';
import { Routes, Route } from 'react-router-dom';
import Home from '../pages/Home';
import HomeV2 from '../pages/HomeV2';
import TrackingListManagerView from '../pages/TrackingListManagerView';
import TrackingListUserView from '../pages/TrackingListUserView';
import DailyTracking from '../pages/DailyTracking';
import DailyTrackingV2 from '../pages/DailyTrackingV2';
import Customers from '../pages/Customers';
import Contracts from '../pages/Contracts';
import Projects from '../pages/Projects';
import ProjectDetail from '../pages/ProjectDetail';
import ProjectsV2 from '../pages/ProjectsV2';
import ContractsV2 from '../pages/ContractsV2';
import ActivitiesV2 from '../pages/ActivitiesV2';
import ProfileV2 from '../pages/ProfileV2';
import ProjectDetailV2 from '../pages/ProjectDetailV2';

import Users from '../pages/Users';
import UserDetail from '../pages/UserDetail';
import Activities from '../pages/Activities';
import Profile from '../pages/Profile';
import RightSection from './RightSection';
import MonthlyManagerView from '../pages/MonthlyManagerView';
import MonthlyManagerViewV2 from '../pages/MonthlyManagerViewV2';
import MonthlyProjectDetail from '../pages/MonthlyProjectDetail';
import MonthlyUserView from '../pages/MonthlyUserView';
import MonthlyPeopleManagerView from '../pages/MonthlyPeopleManagerView';
import MonthlyPeopleManagerViewV2 from '../pages/MonthlyPeopleManagerViewV2';
import MonthlyPeopleManagerDetail from '../pages/MonthlyPeopleManagerDetail';
import TrackingListPeopleManagerView from '../pages/TrackingListPeopleManagerView';
import WorklogsV2 from '../pages/WorklogsV2';
import UsersV2 from '../pages/UsersV2';
import MyMonthV2 from '../pages/MyMonthV2';
import CustomersV2 from '../pages/CustomersV2';
import NotFound from '../pages/NotFound';
import Login from '../pages/Login';
import ProjectReporting from '../pages/ProjectReporting';
import ProtectedRoute from './ProtectedRoute';

import "../App.css";
import { createContext } from 'react';
import { API_ENDPOINTS } from '../config/api.config';
import { DEV_USER, DEV_USER_ID, ENABLE_TAB_CONTRACT, ENABLE_HOME_V2, ENABLE_MONTHLY_MNG_VIEW_V2, ENABLE_MONTHLY_PM_VIEW_V2, ENABLE_COMPACT_SHELL, ENABLE_DAILY_TRACKING_V2, ENABLE_NEW_SIDEBAR, ENABLE_WORKLOGS_V2, ENABLE_USERS_V2, ENABLE_MY_MONTH_V2, ENABLE_CUSTOMERS_V2, ENABLE_PROJECTS_V2, ENABLE_CONTRACTS_V2, ENABLE_ACTIVITIES_V2, ENABLE_PROFILE_V2, getCurrentEnvironment } from '../config/local.config';
import { HAS_V2_PAGES, isV2BannerSnoozed, readThemePref, readV2Pages, snoozeV2Banner, V2_PAGES, writeThemePref, writeV2Pages } from '../utils/v2Pages';
import UserMenu from './UserMenu';
import { useCognito } from '../hooks/useCognito';
import { hasMenuSectionAccess, hasMenuItemAccess, hasPageAccess, getRoleDisplayName } from '../utils/roleManager';
import {
  Category as CategoryIcon,
  EditCalendar as TrackHoursIcon,
  FactCheck as ProjectWorklogsIcon,
  FolderOpen as ProjectsIcon,
  Groups as PeopleOverviewIcon,
  Insights as ProjectsOverviewIcon,
  ListAlt as MyWorklogsIcon,
  ManageAccounts as UsersIcon,
  PlaylistAddCheck as PeopleWorklogsIcon,
  Business as CustomersIcon,
} from '@mui/icons-material';

export const UserContext = createContext();

const drawerWidth = 240;
const drawerCollapsedWidth = 64;

// Sfondo e hover seguono il tema (nero nella modalità OLED), con i colori predefiniti come riserva
const SIDEBAR = {
  primary: '#5ab031',
  bg: 'var(--rt-sidebar-bg, #2a3238)',
  hover: 'var(--rt-sidebar-hover, #3a4550)',
  activeBg: 'rgba(90, 176, 49, 0.15)',
  text: '#e8eaed',
  muted: '#9aa5b1',
};

const drawerPaperSx = {
  boxSizing: 'border-box',
  bgcolor: SIDEBAR.bg,
  borderRight: 'var(--rt-sidebar-border, none)',
  boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
};

// Nuova sidebar su desktop: "flottante", staccata dai bordi, con angoli arrotondati, leggera luce dall'alto e ombra
// che la solleva dalla pagina. In OLED niente luce e un bordo al posto dell'ombra (variabili in theme/tokens.js).
// È attiva solo con il nuovo design (newSidebar nel componente).
const SIDEBAR_GAP = 12;
// Marchio Avvale in trasparenza in basso a destra: grande, tagliato dal bordo e dietro le card (solo desktop)
const watermarkSx = {
  display: { xs: 'none', sm: 'block' },
  position: 'fixed',
  right: -90,
  bottom: -100,
  width: 600,
  height: 600,
  backgroundImage: 'url(/brand/avvale-mark.png)',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  opacity: 'var(--rt-watermark-opacity, 0.07)',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: -1,
};
const floatingPaperSx = {
  top: SIDEBAR_GAP,
  left: SIDEBAR_GAP,
  bottom: SIDEBAR_GAP,
  height: 'auto',
  borderRadius: '18px',
  border: 'var(--rt-sidebar-border, 1px solid rgba(255,255,255,0.06))',
  backgroundImage: 'var(--rt-sidebar-sheen, linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 40%))',
  boxShadow: 'var(--rt-sidebar-shadow, 0 24px 48px -20px rgba(15,23,18,0.5), 0 8px 18px rgba(15,23,18,0.18))',
};

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const THEMES = { light: buildAppTheme('light'), dark: buildAppTheme('dark'), oled: buildAppTheme('oled') };

export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(() => !window.matchMedia('(max-width:600px)').matches);
  const [rightOpen, setRightOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Gestione autenticazione Cognito
  const { user: cognitoUser, backendUser, loading: authLoading, isAuthenticated, logout: cognitoLogout } = useCognito();

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  // Scelta fatta in questa sessione per le pagine V2 (per utente); senza scelta vale quella salvata
  const [v2Choice, setV2Choice] = useState({ userKey: null, enabled: false });
  // Tema scelto in questa sessione (per utente); senza scelta vale quello salvato
  const [themeChoice, setThemeChoice] = useState({ userKey: null, value: 'light' });
  // Invito al nuovo design rimandato in questa sessione (per utente); senza scelta vale quella salvata
  const [bannerChoice, setBannerChoice] = useState({ userKey: null, snoozed: false });
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    async function fetchUsers() {
      setUserLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.USERS);
        const data = await res.json();
        let arr = data.users || [];
        setUsers(Array.isArray(arr) ? arr : []);
        
        // In development mode, force selectedUser to DEV_USER_ID
        if (process.env.NODE_ENV === 'development') {
          setSelectedUser(DEV_USER_ID);
        } else {
          // Se c'è un utente del backend autenticato, usalo come selectedUser
          if (backendUser && backendUser.id) {
            setSelectedUser(backendUser.id);
          } else {
            // Non impostare un utente di default se non c'è autenticazione
            // Questo evita di mostrare dati di altri utenti
            setSelectedUser('');
          }
        }
      } catch {
        setUsers([]);
      } finally {
        setUserLoading(false);
      }
    }
    fetchUsers();
  }, [backendUser]);

  const menuItems = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    // My Area
    { section: "My Area" },
    { text: "Daily Tracking", icon: <AccessTimeIcon />, path: "/daily-tracking" },
    { text: "Monthly View", icon: <CalendarIcon />, path: "/monthly-user-view" },
    { text: "List View", icon: <ListIcon />, path: "/tracking-list-user" },
    // Manager Area
    { section: "Manager Area" },
    { text: "Monthly Tracking View", icon: <EventAvailable />, path: "/monthly-mng-view" },
    { text: "List Tracking View", icon: <ListIcon />, path: "/list-tracking-view" },
    { separator: true }, 
    { text: "Projects", icon: <InventoryIcon />, path: "/projects" },
    { text: "Contracts", icon: <DescriptionIcon />, path: "/contracts" },
    { text: "Customers", icon: <Badge />, path: "/customers" },
    
    // People Manager Area - visible only to users with org_role_id = 2
    { section: "People Manager Area", requiresOrgRole: 2 },
    { text: "Monthly PM View", icon: <EventAvailable />, path: "/monthly-people-mng-view", requiresOrgRole: 2 },
    { text: "List PM View", icon: <ListIcon />, path: "/list-tracking-people-mng-view", requiresOrgRole: 2 },
    
    // Admin Area
    { section: "Admin Area" },
    { text: "Users", icon: <PeopleIcon />, path: "/users" },
    { text: "Activities", icon: <ListIcon />, path: "/activities" },
    { text: "Project Reporting", icon: <AssessmentIcon />, path: "/project-reporting" },
  ];

  // Nuova sidebar: stesse sezioni e stesso ordine, nomi per attività e icone distinte.
  // `text` resta la chiave dei permessi di menu, `label` è il nome mostrato; le pagine V2 non sono elencate.
  const newMenuItems = [
    { text: "Home", label: "Home", icon: <HomeIcon />, path: "/" },
    { section: "My work", sectionKey: "My Area" },
    { text: "Daily Tracking", label: "Track hours", icon: <TrackHoursIcon />, path: "/daily-tracking" },
    { text: "Monthly View", label: "My month", icon: <CalendarIcon />, path: "/monthly-user-view" },
    { text: "List View", label: "My worklogs", icon: <MyWorklogsIcon />, path: "/tracking-list-user" },
    { section: "Projects", sectionKey: "Manager Area" },
    { text: "Monthly Tracking View", label: "Projects overview", icon: <ProjectsOverviewIcon />, path: "/monthly-mng-view" },
    { text: "List Tracking View", label: "Project worklogs", icon: <ProjectWorklogsIcon />, path: "/list-tracking-view" },
    { section: "Setup", sectionKey: "Manager Area" },
    { text: "Projects", label: "Projects", icon: <ProjectsIcon />, path: "/projects" },
    { text: "Contracts", label: "Contracts", icon: <DescriptionIcon />, path: "/contracts" },
    { text: "Customers", label: "Customers", icon: <CustomersIcon />, path: "/customers" },
    { section: "My people", requiresOrgRole: 2 },
    { text: "Monthly PM View", label: "People overview", icon: <PeopleOverviewIcon />, path: "/monthly-people-mng-view", requiresOrgRole: 2 },
    { text: "List PM View", label: "People worklogs", icon: <PeopleWorklogsIcon />, path: "/list-tracking-people-mng-view", requiresOrgRole: 2 },
    { section: "Admin", sectionKey: "Admin Area" },
    { text: "Users", label: "Users", icon: <UsersIcon />, path: "/users" },
    { text: "Activities", label: "Activities", icon: <CategoryIcon />, path: "/activities" },
    { text: "Project Reporting", label: "Project reporting", icon: <AssessmentIcon />, path: "/project-reporting" },
  ];
  // Filtra le voci per ruolo: una voce compare solo se l'utente può aprire la pagina;
  // sezioni e separatori senza voci visibili sotto vengono tolti.
  const getFilteredMenuItems = () => {
    // Contracts/Revenues dietro flag (ENABLE_TAB_CONTRACT): spento in produzione
    const activeMenuItems = (newSidebar ? newMenuItems : menuItems)
      .filter((item) => ENABLE_TAB_CONTRACT || item.path !== '/contracts');
    if (!backendUser) {
      // In development mode, show all items
      if (process.env.NODE_ENV === 'development') {
        return activeMenuItems;
      }
      // In production, show only Home if user is authenticated but not in database
      if (isAuthenticated && cognitoUser) {
        return activeMenuItems.filter((item) => item.path === '/');
      }
      return []; // Hide all menu items if no user data
    }

    const userRole = Number(backendUser.role_id);
    const orgRoleId = Number(backendUser.org_role_id); // org_role_id per People Manager Area
    const visible = activeMenuItems.filter((item) => {
      if (item.requiresOrgRole !== undefined) return orgRoleId === item.requiresOrgRole;
      if (item.section) return hasMenuSectionAccess(userRole, item.sectionKey || item.section);
      if (item.separator) return true;
      return hasMenuItemAccess(userRole, item.text) && hasPageAccess(userRole, item.path);
    });

    return visible.filter((item, idx) => {
      if (!item.section && !item.separator) return true;
      const next = visible[idx + 1];
      return !!next && !next.section && !next.separator;
    });
  };

  // User menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  // Logout con Cognito
  const handleLogout = () => {
    setAnchorEl(null);
    cognitoLogout();
  };
  // Ricava nome/cognome/iniziali (mantenuto per compatibilità se necessario)
  let userObj = users.find(u => String(u.id) === String(selectedUser));
  let userName = userObj ? (userObj.name || userObj.username || userObj.email || "") : "";
  let userSurname = userObj ? (userObj.surname || "") : "";
  let initials = null;
  if (userName && userSurname) {
    initials = `${userName.trim()[0] || ''}${userSurname.trim()[0] || ''}`.toUpperCase();
  } else if (userName) {
    initials = userName.trim()[0].toUpperCase();
  } else if (userSurname) {
    initials = userName.trim()[0].toUpperCase();
  }
  let isLoggedIn = !!userObj;

  // Le pagine V2 (non elencate) evidenziano la voce della pagina corrispondente
  const activePath = location.pathname === '/home-v2' ? '/' : location.pathname.replace(/-v2$/, '');
  const renderNavItem = (item) => {
    const isSelected = activePath === item.path;
    const label = item.label || item.text;
    const button = (
      <ListItemButton
        selected={isSelected}
        onClick={() => navigate(item.path)}
        sx={{
          minHeight: 40,
          mx: newSidebar ? 1 : 0.5,
          my: newSidebar ? 0.375 : 0.25,
          borderRadius: newSidebar ? '12px' : 1,
          justifyContent: open ? 'initial' : 'center',
          pl: open ? (newSidebar ? 1 : 0.5) : 1,
          pr: open ? 1 : 1,
          color: SIDEBAR.text,
          ...(newSidebar
            ? {
                // voce attiva: pillola verde con un filo di luce, invece della barra a sinistra
                boxShadow: isSelected ? 'inset 0 0 0 1px rgba(90,176,49,0.35), 0 4px 12px -4px rgba(90,176,49,0.35)' : 'none',
                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
              }
            : { borderLeft: `3px solid ${isSelected ? SIDEBAR.primary : 'transparent'}` }),
          bgcolor: isSelected ? SIDEBAR.activeBg : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? SIDEBAR.activeBg : SIDEBAR.hover,
          },
          '&.Mui-selected': {
            bgcolor: SIDEBAR.activeBg,
            '&:hover': {
              bgcolor: SIDEBAR.activeBg,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 32,
            mr: open ? 0.5 : 0,
            justifyContent: 'center',
            color: isSelected ? SIDEBAR.primary : SIDEBAR.muted,
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
            },
          }}
        >
          {item.icon}
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
              fontFamily: "'Archivo', sans-serif",
              noWrap: true,
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
        {open ? button : (
          <Tooltip title={label} placement="right" arrow>
            {button}
          </Tooltip>
        )}
      </ListItem>
    );
  };

  const environment = getCurrentEnvironment();
  const envLabel = environment === 'labs' ? 'LABS' : environment === 'development' ? 'DEV' : null;
  const devMode = process.env.NODE_ENV === 'development';
  const shellUser = devMode
    ? backendUser || DEV_USER
    : backendUser;
  const shellEmail = devMode ? shellUser?.email || 'dev@example.com' : cognitoUser?.email;
  const shellRole = shellUser ? getRoleDisplayName(shellUser.role_id) : '';

  // Pagine V2: con l'interruttore acceso sostituiscono le classiche sugli stessi percorsi
  const v2UserKey = shellEmail;
  const v2Pages = v2Choice.userKey === v2UserKey ? v2Choice.enabled : readV2Pages(v2UserKey);
  const v2BannerSnoozed = bannerChoice.userKey === v2UserKey ? bannerChoice.snoozed : isV2BannerSnoozed(v2UserKey);
  const snoozeBanner = () => {
    snoozeV2Banner(v2UserKey);
    setBannerChoice({ userKey: v2UserKey, snoozed: true });
  };
  const toggleV2Pages = () => {
    const enabled = !v2Pages;
    writeV2Pages(v2UserKey, enabled);
    setV2Choice({ userKey: v2UserKey, enabled });
    // Chi torna al design classico non rivede subito l'invito a provare quello nuovo
    if (!enabled) snoozeBanner();
    // Spegnendo da un indirizzo V2 si torna alla pagina classica corrispondente
    const current = V2_PAGES.find((p) => p.v2Path === location.pathname);
    if (!enabled && current) navigate(`${current.path}${location.search}`);
  };
  const withV2 = (enabled, v2Element, classicElement) => (v2Pages && enabled ? v2Element : classicElement);
  // Sidebar nuova e shell compatta fanno parte del nuovo design: seguono l'interruttore, così chi resta sulle pagine
  // classiche vede l'applicazione com'era. Shell compatta: niente barra superiore su desktop; marchio, ambiente e
  // utente nel menu laterale.
  const newSidebar = ENABLE_NEW_SIDEBAR && v2Pages;
  const compactShell = ENABLE_COMPACT_SHELL && v2Pages;

  // Tema scuro: solo con le pagine nuove attive e sulle pagine che hanno una versione V2 (le classiche restano chiare)
  const themePref = themeChoice.userKey === v2UserKey ? themeChoice.value : readThemePref(v2UserKey);
  const changeThemePref = (value) => {
    writeThemePref(v2UserKey, value);
    setThemeChoice({ userKey: v2UserKey, value });
  };
  const darkCapablePage = V2_PAGES.some(
    (p) =>
      location.pathname === p.path ||
      location.pathname === p.v2Path ||
      (p.path === '/users' && location.pathname.startsWith('/users/')) ||
      (p.path === '/projects' && /^\/projects(-v2)?\/\d+(\/[a-z]+)?$/.test(location.pathname)),
  );
  let themeMode = 'light';
  if (v2Pages && darkCapablePage) {
    if (themePref === 'dark' || themePref === 'oled') themeMode = themePref;
    else if (themePref === 'system' && systemDark) themeMode = 'dark';
  }

  const userMenuProps = {
    user: shellUser,
    email: shellEmail,
    roleLabel: shellRole,
    notInDatabase: !devMode && isAuthenticated && !backendUser,
    colors: SIDEBAR,
    onNavigate: navigate,
    v2Pages: HAS_V2_PAGES ? v2Pages : undefined,
    onToggleV2Pages: HAS_V2_PAGES ? toggleV2Pages : undefined,
    themePref: HAS_V2_PAGES && v2Pages ? themePref : undefined,
    onThemePrefChange: changeThemePref,
  };
  const envBadge = envLabel && (
    <Box
      component="span"
      sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', px: 0.75, py: 0.1, borderRadius: 0.75, bgcolor: '#f9a825', color: '#3e2a00' }}
    >
      {envLabel}
    </Box>
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          position: 'relative',
          minHeight: 64,
          borderBottom: newSidebar ? 'none' : '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: open ? 'row' : 'column',
            gap: open ? 0 : 0.5,
            minHeight: 64,
            px: open ? 5 : 1,
            py: open ? 0 : 1,
          }}
        >
          {compactShell && open ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 1 }}>
              <img src="/logo.png" alt="Avvale" style={{ height: 26, maxWidth: 140, objectFit: 'contain', display: 'block' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, fontStyle: 'italic', color: SIDEBAR.primary, lineHeight: 1.2 }}>
                  RUNTime
                </Typography>
                {envBadge}
              </Box>
            </Box>
          ) : (
            <img
              src={open ? '/logo.png' : '/logo_small.png'}
              alt="Logo"
              style={{
                height: open ? 28 : 22,
                maxWidth: open ? 140 : 32,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          {compactShell && !open && envBadge}
          {!open && (
            <IconButton
              onClick={handleDrawerOpen}
              size="small"
              aria-label="Apri menu"
              sx={{
                color: SIDEBAR.muted,
                bgcolor: newSidebar ? 'rgba(255,255,255,0.05)' : undefined,
                '&:hover': {
                  color: SIDEBAR.text,
                  bgcolor: SIDEBAR.hover,
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {open && (
          <IconButton
            onClick={handleDrawerClose}
            size="small"
            aria-label="Close menu"
            sx={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              color: SIDEBAR.muted,
              bgcolor: newSidebar ? 'rgba(255,255,255,0.05)' : undefined,
              '&:hover': {
                color: SIDEBAR.text,
                bgcolor: SIDEBAR.hover,
              },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <List
        sx={{
          py: 1,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
          },
        }}
      >
        {getFilteredMenuItems().map((item, idx) => {
          if (item.section) {
            if (open) {
              return (
                <ListSubheader
                  key={item.section}
                  disableSticky
                  sx={{
                    bgcolor: 'transparent',
                    color: SIDEBAR.muted,
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: 2,
                    pl: newSidebar ? 2.25 : 1.25,
                    pt: 1.5,
                    pb: 0.5,
                  }}
                >
                  {item.section}
                </ListSubheader>
              );
            }
            return (
              <Tooltip key={item.section} title={item.section} placement="right" arrow>
                <Box sx={{ px: 1.5, py: 0.75, cursor: 'default' }}>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
                </Box>
              </Tooltip>
            );
          }
          if (item.separator) {
            if (!open) {
              return (
                <Box key={`separator-${idx}`} sx={{ px: 1.5, py: 0.75 }}>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
                </Box>
              );
            }
            return <Box key={`separator-${idx}`} sx={{ my: 1 }} />;
          }
          return renderNavItem(item);
        })}
      </List>
      {compactShell && (isAuthenticated || devMode) && (
        <Box
          sx={
            newSidebar
              ? { m: 1, mt: 0.5, borderRadius: '14px', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)' }
              : { borderTop: '1px solid rgba(255,255,255,0.08)' }
          }
        >
          <UserMenu {...userMenuProps} collapsed={!open} rounded={newSidebar} />
        </Box>
      )}
    </Box>
  );

  // In development mode, always provide mock data
  const isDev = process.env.NODE_ENV === 'development';
  
  // Force selectedUser to DEV_USER_ID in development mode
  const forcedSelectedUser = isDev ? DEV_USER_ID : selectedUser;
  
  // Ensure we always have valid context values
  const contextValue = { 
    selectedUser: forcedSelectedUser, 
    setSelectedUser, 
    users, 
    cognitoUser: isDev 
      ? { email: 'dev@example.com', name: 'Developer', username: 'devuser', surname: 'User', sub: 'mock-sub-123', groups: ['developers'] }
      : (cognitoUser || null), 
    // In development, usa il backendUser proveniente da useCognito (mock con role_id e org_role_id corretti)
    backendUser: isDev
      ? (backendUser || DEV_USER)
      : (backendUser || null),
    isAuthenticated: isDev ? true : (isAuthenticated || false), 
    authLoading: isDev ? false : (authLoading || false),
    // In development, non forzare Admin: usa il role_id del backendUser mock
    // In development il ruolo è subito quello dell'utente finto: niente passaggio intermedio da Admin
    userRole: isDev ? (backendUser?.role_id || DEV_USER.role_id) : (backendUser?.role_id || null),
    // Preferenze dell'utente (le stesse del menu utente), usate anche dalla pagina Profile
    preferences: {
      v2Available: HAS_V2_PAGES,
      v2Pages,
      toggleV2Pages,
      themePref,
      setThemePref: changeThemePref,
      bannerSnoozed: v2BannerSnoozed,
      snoozeBanner,
    },
  };

  return (
    <ThemeProvider theme={THEMES[themeMode]}>
      <CssBaseline />
      <GlobalStyles styles={{ ':root': cssVariables(themeMode) }} />
    <UserContext.Provider value={contextValue}>
                      <Box sx={{ display: "flex" }}>
          {compactShell && isMobile && (
            <AppBar position="fixed" elevation={0} sx={{ bgcolor: SIDEBAR.bg }}>
              <Toolbar variant="dense" sx={{ gap: 1 }}>
                <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerOpen}>
                  <MenuIcon />
                </IconButton>
                <Typography sx={{ fontSize: 18, fontWeight: 700, fontStyle: 'italic', color: SIDEBAR.primary }}>RUNTime</Typography>
                {envBadge}
                <Box sx={{ flexGrow: 1 }} />
                {(isAuthenticated || devMode) && <UserMenu {...userMenuProps} variant="avatar" />}
              </Toolbar>
            </AppBar>
          )}
          {!compactShell && (
          <AppBar
            position="fixed"
            sx={{
              bgcolor: '#5ab031',
              transition: 'width 0.2s, margin-left 0.2s',
              ml: { sm: open ? `${drawerWidth}px` : 0 },
              width: { sm: open ? `calc(100% - ${drawerWidth}px)` : '100%' }
            }}
          >
          <Toolbar>
            { !open && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerOpen}
                sx={{ mr: 2, ml: { xs: 0, sm: `${drawerCollapsedWidth + 8}px` } }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', fontSize: '28px', textAlign: 'center', marginLeft: '24px', fontWeight: 700, fontStyle: 'italic' }}>
              RUNTime
            </Typography>

            
            {/* User/Login menu */}
            {isAuthenticated && cognitoUser ? (
              <>
                <IconButton onClick={handleMenuOpen} sx={{ ml: 2 }}>
                  <Avatar sx={{ bgcolor: '#333c44', width: 36, height: 36 }}>
                    {(() => {
                      // Genera iniziali dal backendUser
                      if (backendUser) {
                        const name = backendUser.name || backendUser.username || '';
                        const surname = backendUser.surname || '';
                        
                        if (name && surname) {
                          return `${name.trim()[0] || ''}${surname.trim()[0] || ''}`.toUpperCase();
                        } else if (name) {
                          return name.trim()[0].toUpperCase();
                        } else if (surname) {
                          return surname.trim()[0].toUpperCase();
                        }
                      }
                      
                      // Fallback all'icona generica
                      return <AccountCircle sx={{ color: 'white', width: 28, height: 28 }} />;
                    })()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {/* Indicatore stato autenticazione */}
                  {isAuthenticated && cognitoUser && (
                    <MenuItem disabled sx={{ 
                      bgcolor: backendUser ? 'success.main' : 'error.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: 1,
                      gap: 0.5
                    }}>
                      {cognitoUser.email}
                      {!backendUser && ' (Not in DB)'}
                    </MenuItem>
                  )}
                  {authLoading && (
                    <MenuItem disabled sx={{ 
                      bgcolor: 'warning.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      🔄 Checking auth...
                    </MenuItem>
                  )}
                  {!isAuthenticated && !authLoading && (
                    <MenuItem disabled sx={{ 
                      bgcolor: 'error.main', 
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      ❌ Not authenticated
                    </MenuItem>
                  )}
                  
                  <Divider />
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
                    {backendUser ? (backendUser.name || backendUser.username || backendUser.email) : (cognitoUser.name || cognitoUser.email)}
                  </MenuItem>
                  {backendUser ? (
                    <MenuItem disabled sx={{ 
                      fontSize: '0.75rem', 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}>
                      Role: {getRoleDisplayName(backendUser.role_id)}
                    </MenuItem>
                  ) : (
                    <MenuItem disabled sx={{ 
                      fontSize: '0.75rem', 
                      color: 'error.main',
                      fontStyle: 'italic'
                    }}>
                      User not found in database
                    </MenuItem>
                  )}
                  {/* Nella barra classica non c'è l'interruttore "New pages": da qui si passa al nuovo design */}
                  {HAS_V2_PAGES && (
                    <MenuItem onClick={() => { handleMenuClose(); toggleV2Pages(); }}>
                      Try the new design
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/logout'); }}>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button color="inherit" sx={{ ml: 2, fontWeight: 700 }} onClick={() => navigate('/login')}>
                Login
              </Button>
            )}
          </Toolbar>
        </AppBar>
          )}
        <Box
          component="nav"
          sx={{ width: { sm: open ? drawerWidth + (newSidebar ? SIDEBAR_GAP : 0) : 0 }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={open}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                ...drawerPaperSx,
                width: drawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            open={open}
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                ...drawerPaperSx,
                ...(newSidebar ? floatingPaperSx : {}),
                width: open ? drawerWidth : drawerCollapsedWidth,
                transition: 'width 0.2s',
                overflowX: 'hidden',
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: 3, 
          
          ml: { sm: open ? `${newSidebar ? SIDEBAR_GAP : 10}px` : `${drawerCollapsedWidth + (newSidebar ? 2 * SIDEBAR_GAP : 0)}px` },
          transition: 'margin-left 0.2s',
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {newSidebar && <Box aria-hidden sx={watermarkSx} />}
          {!compactShell ? <DrawerHeader /> : isMobile ? <Toolbar variant="dense" /> : null}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute>{withV2(ENABLE_HOME_V2, <HomeV2 />, <Home />)}</ProtectedRoute>} />
            {ENABLE_HOME_V2 && <Route path="/home-v2" element={<ProtectedRoute><HomeV2 /></ProtectedRoute>} />}
            <Route path="/list-tracking-view" element={<ProtectedRoute>{withV2(ENABLE_WORKLOGS_V2, <WorklogsV2 key="projects" scope="projects" />, <TrackingListManagerView />)}</ProtectedRoute>} />
            {ENABLE_WORKLOGS_V2 && <Route path="/list-tracking-view-v2" element={<ProtectedRoute><WorklogsV2 key="projects" scope="projects" /></ProtectedRoute>} />}
            <Route path="/daily-tracking" element={<ProtectedRoute>{withV2(ENABLE_DAILY_TRACKING_V2, <DailyTrackingV2 />, <DailyTracking />)}</ProtectedRoute>} />
            {ENABLE_DAILY_TRACKING_V2 && <Route path="/daily-tracking-v2" element={<ProtectedRoute><DailyTrackingV2 /></ProtectedRoute>} />}
            <Route path="/monthly-user-view" element={<ProtectedRoute>{withV2(ENABLE_MY_MONTH_V2, <MyMonthV2 />, <MonthlyUserView />)}</ProtectedRoute>} />
            {ENABLE_MY_MONTH_V2 && <Route path="/monthly-user-view-v2" element={<ProtectedRoute><MyMonthV2 /></ProtectedRoute>} />}
            <Route path="/tracking-list-user" element={<ProtectedRoute>{withV2(ENABLE_WORKLOGS_V2, <WorklogsV2 key="mine" scope="mine" />, <TrackingListUserView />)}</ProtectedRoute>} />
            {ENABLE_WORKLOGS_V2 && <Route path="/tracking-list-user-v2" element={<ProtectedRoute><WorklogsV2 key="mine" scope="mine" /></ProtectedRoute>} />}
            <Route path="/monthly-mng-view" element={<ProtectedRoute>{withV2(ENABLE_MONTHLY_MNG_VIEW_V2, <MonthlyManagerViewV2 />, <MonthlyManagerView />)}</ProtectedRoute>} />
            {ENABLE_MONTHLY_MNG_VIEW_V2 && <Route path="/monthly-mng-view-v2" element={<ProtectedRoute><MonthlyManagerViewV2 /></ProtectedRoute>} />}
            <Route path="/monthly-mng-view/project/:projectId" element={<ProtectedRoute><MonthlyProjectDetail /></ProtectedRoute>} />
            <Route
              path="/monthly-people-mng-view"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  {withV2(ENABLE_MONTHLY_PM_VIEW_V2, <MonthlyPeopleManagerViewV2 />, <MonthlyPeopleManagerView />)}
                </ProtectedRoute>
              }
            />
            {ENABLE_MONTHLY_PM_VIEW_V2 && (
              <Route
                path="/monthly-people-mng-view-v2"
                element={
                  <ProtectedRoute requiredOrgRole={2}>
                    <MonthlyPeopleManagerViewV2 />
                  </ProtectedRoute>
                }
              />
            )}
            <Route
              path="/monthly-people-mng-view/user/:userId"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  <MonthlyPeopleManagerDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/list-tracking-people-mng-view"
              element={
                <ProtectedRoute requiredOrgRole={2}>
                  {withV2(ENABLE_WORKLOGS_V2, <WorklogsV2 key="people" scope="people" />, <TrackingListPeopleManagerView />)}
                </ProtectedRoute>
              }
            />
            {ENABLE_WORKLOGS_V2 && (
              <Route
                path="/list-tracking-people-mng-view-v2"
                element={
                  <ProtectedRoute requiredOrgRole={2}>
                    <WorklogsV2 key="people" scope="people" />
                  </ProtectedRoute>
                }
              />
            )}
            <Route path="/project-reporting" element={<ProtectedRoute><ProjectReporting /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute>{withV2(ENABLE_CUSTOMERS_V2, <CustomersV2 />, <Customers />)}</ProtectedRoute>} />
            {ENABLE_CUSTOMERS_V2 && <Route path="/customers-v2" element={<ProtectedRoute><CustomersV2 /></ProtectedRoute>} />}
            {ENABLE_TAB_CONTRACT && <Route path="/contracts" element={<ProtectedRoute>{withV2(ENABLE_CONTRACTS_V2, <ContractsV2 />, <Contracts />)}</ProtectedRoute>} />}
            {ENABLE_TAB_CONTRACT && ENABLE_CONTRACTS_V2 && <Route path="/contracts-v2" element={<ProtectedRoute><ContractsV2 /></ProtectedRoute>} />}
            <Route path="/projects" element={<ProtectedRoute>{withV2(ENABLE_PROJECTS_V2, <ProjectsV2 />, <Projects />)}</ProtectedRoute>} />
            {/* Con Projects V2 la scheda aperta è nel percorso (/projects/:id/mapping) */}
            <Route path={ENABLE_PROJECTS_V2 ? '/projects/:id/:tab?' : '/projects/:id'} element={<ProtectedRoute>{withV2(ENABLE_PROJECTS_V2, <ProjectDetailV2 />, <ProjectDetail />)}</ProtectedRoute>} />
            {ENABLE_PROJECTS_V2 && <Route path="/projects-v2" element={<ProtectedRoute><ProjectsV2 /></ProtectedRoute>} />}
            {ENABLE_PROJECTS_V2 && <Route path="/projects-v2/:id/:tab?" element={<ProtectedRoute><ProjectDetailV2 /></ProtectedRoute>} />}
            <Route path="/users" element={<ProtectedRoute>{withV2(ENABLE_USERS_V2, <UsersV2 />, <Users />)}</ProtectedRoute>} />
            <Route path="/users/:id" element={<ProtectedRoute>{withV2(ENABLE_USERS_V2, <UsersV2 />, <UserDetail />)}</ProtectedRoute>} />
            {ENABLE_USERS_V2 && <Route path="/users-v2" element={<ProtectedRoute><UsersV2 /></ProtectedRoute>} />}
            <Route path="/activities" element={<ProtectedRoute>{withV2(ENABLE_ACTIVITIES_V2, <ActivitiesV2 />, <Activities />)}</ProtectedRoute>} />
            {ENABLE_ACTIVITIES_V2 && <Route path="/activities-v2" element={<ProtectedRoute><ActivitiesV2 /></ProtectedRoute>} />}
            <Route path="/profile" element={<ProtectedRoute>{withV2(ENABLE_PROFILE_V2, <ProfileV2 />, <Profile />)}</ProtectedRoute>} />
            {ENABLE_PROFILE_V2 && <Route path="/profile-v2" element={<ProtectedRoute><ProfileV2 /></ProtectedRoute>} />}
            <Route path="*" element={<NotFound />} />
          </Routes>
          {rightOpen && (
            <Box
              sx={{
                width: 320,
                height: '100vh',
                position: 'fixed',
                right: 0,
                top: 0,
                bgcolor: 'white',
                borderLeft: '1px solid #ddd',
                zIndex: 1200,
                boxShadow: 3,
                p: 2
              }}
            >
              <RightSection />
            </Box>
          )}
        </Box>
      </Box>
    </UserContext.Provider>
    </ThemeProvider>
  );
}